import { IconChevronDown } from "@tabler/icons-react"
import { type FC, type ReactNode, useState } from "react"

import Collapse from "@forento/ui/components/Collapse"
import { cn } from "@forento/ui/utilities/tailwind"

const collapsedStorageKeyPrefix = "website-editor-section-collapsed:"

export const useSidebarSectionCollapse = (section: string): [boolean, () => void] => {
	const [isCollapsed, setCollapsed] = useState(
		() => localStorage.getItem(`${collapsedStorageKeyPrefix}${section}`) === "true",
	)

	const toggle = () => {
		const next = !isCollapsed
		setCollapsed(next)
		localStorage.setItem(`${collapsedStorageKeyPrefix}${section}`, next.toString())
	}

	return [isCollapsed, toggle]
}

type Props = {
	title: ReactNode
	detail?: ReactNode
	subtitle?: ReactNode
	isCollapsed: boolean
	onToggle: () => void
	children: ReactNode
}
const SidebarSection: FC<Props> = ({ title, detail, subtitle, isCollapsed, onToggle, children }) => (
	<div className="flex flex-col">
		<button
			type="button"
			onClick={onToggle}
			aria-expanded={!isCollapsed}
			className="flex cursor-pointer items-center justify-between gap-3 text-left"
		>
			<div className="min-w-0 truncate text-xs font-bold tracking-wide text-gray-500 uppercase">{title}</div>
			<div className="flex shrink-0 items-center gap-2">
				{detail !== undefined && <div className="text-xs font-semibold text-gray-400">{detail}</div>}
				<IconChevronDown
					className={cn("size-4 shrink-0 text-gray-400 transition-transform", isCollapsed && "-rotate-90")}
				/>
			</div>
		</button>
		{/* The side padding leaves room for focus rings and the page list's negative margin, which
		    would otherwise be clipped by the collapse container. */}
		<Collapse isCollapsed={isCollapsed} className="-mx-1 px-1">
			{/* Collapsed content is only clipped to height 0, so it is made inert to keep keyboard
			    focus from landing in it and scrolling the hidden container. */}
			{subtitle !== undefined && (
				<div inert={isCollapsed} className="pt-1 text-xs text-gray-400">
					{subtitle}
				</div>
			)}
			<div inert={isCollapsed} className="flex flex-col gap-4 pt-5">
				{children}
			</div>
		</Collapse>
	</div>
)

export default SidebarSection
